'use client';

import {
  FilterBar,
  type TFilterBarPriceRange,
} from '@chemist4u/newt-components';
import { type SortOptions, sortOptions } from '@lib/util/sort-products';
import { PRODUCTS_PAGE_SIZE } from '@modules/store/lib/product-query';

type ProductListingToolbarProps = {
  sortBy: SortOptions;
  productCount: number;
  minPrice?: number;
  maxPrice?: number;
  /** True while a client-side sort/filter override is fetching its first
   * page — `productCount` is 0 at that point, so the results label would
   * otherwise misleadingly read "Showing 0 products". */
  isLoading?: boolean;
  onSortChange: (value: string) => void;
  onPriceApply: (range: TFilterBarPriceRange) => void;
};

// Purely controlled sibling of `ProductToolbar` — that component owns its
// own URL sync via `useSearchParams()`, which is fine for the SSR search
// templates (already fully dynamic routes) but would force `ProductListing`
// back into the Suspense/no-JS-fallback bind this component exists to avoid
// on static/ISR category & collection pages. This one takes no routing
// dependency at all; the caller owns sort/price state and the URL sync.
const ProductListingToolbar = ({
  sortBy,
  productCount,
  minPrice,
  maxPrice,
  isLoading = false,
  onSortChange,
  onPriceApply,
}: ProductListingToolbarProps) => {
  return (
    <div className="relative z-[25] mb-4">
      <FilterBar
        page={1}
        pageSize={PRODUCTS_PAGE_SIZE}
        totalItems={productCount}
        resultsLabel={
          isLoading
            ? 'Loading products…'
            : `Showing ${productCount} ${productCount === 1 ? 'product' : 'products'}`
        }
        sortValue={sortBy}
        sortOptions={sortOptions}
        onSortChange={onSortChange}
        priceMin={minPrice ?? null}
        priceMax={maxPrice ?? null}
        onPriceApply={onPriceApply}
      />
    </div>
  );
};

export default ProductListingToolbar;
